import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cx } from '@/utils/cx';
import styles from '@/styles/components/TextField/style.scss';

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'> {
  /** Visible label. Omit only when an aria-label is given. */
  label?: string;
  /** Content inside the box before the input (icon, "₹", "+91"). */
  prefix?: ReactNode;
  /** Content inside the box after the input (e.g. a show-password button). */
  suffix?: ReactNode;
  /** Helper or validation text under the field. */
  hint?: string;
  tone?: 'default' | 'warning';
  mono?: boolean;
  size?: 'md' | 'lg';
  fieldClassName?: string;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, prefix, suffix, hint, tone = 'default', mono, size = 'lg', id, className, fieldClassName, ...input },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const hintId = hint ? `${inputId}-hint` : undefined;

  return (
    <div className={cx(styles.wrapper, className)}>
      {label && (
        <label htmlFor={inputId} className={styles.label}>
          {label}
        </label>
      )}
      <div className={cx(styles.box, styles[size], tone === 'warning' && styles.warningBox, fieldClassName)}>
        {prefix && <span className={styles.prefix}>{prefix}</span>}
        <input
          ref={ref}
          id={inputId}
          aria-describedby={hintId}
          className={cx(styles.input, mono && styles.mono)}
          {...input}
        />
        {suffix && <span className={styles.suffix}>{suffix}</span>}
      </div>
      {hint && (
        <span id={hintId} className={cx(styles.hint, tone === 'warning' && styles.warningHint)}>
          {hint}
        </span>
      )}
    </div>
  );
});
